import React from "react";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DealChip } from "~/components/ui/DealChip";
import { cn, formatPrice, formatKm, type DealRating, type ReportingCurrency } from "~/lib/utils";
import { useT } from "~/lib/i18n";
import { trpc } from "~/lib/trpc";
import { CompareToggleButton, useCompareOptional, type CompareDeal } from "~/components/scan/compare";
import { computeSignals, daysOnMarket, type Signal } from "~/lib/signals";

interface DealFeatures {
  lift: boolean;
  ccm: boolean;
  magnaride: boolean;
  sportExhaust: boolean;
  carbonExt: boolean;
  carbonSeats: boolean;
  carbonInterior: boolean;
  interior: string | null;
  paint: string | null;
  confidence: string | null;
}

interface Deal extends CompareDeal {
  currentDoors?: number | null;
  currentSeats?: number | null;
  currentEngineCc?: number | null;
  currentDriveType?: string | null;
  currentCo2?: number | null;
  currentFirstRegistration?: string | null;
  firstSeenAt?: Date | string | null;
  lastSeenAt: Date | string | null;
  dupCount?: number;
  dupSources?: string[];
  fairPriceConfidence?: string | null;
  dealSavingsRawEur?: number | null;
  savingsSuspect?: boolean;
  recentPriceDropEur?: number | null;
  photoCount?: number | null;
  sellerType?: string | null;
  features?: DealFeatures | null;
}

/**
 * Pick the top 2 most decision-relevant signals to surface as chips on a
 * card. We always show a `risk` first (if any), then prefer cons over pros
 * because the rating chip already conveys the headline pro.
 */
function pickCardSignals(signals: Signal[]): Signal[] {
  const risk = signals.find((s) => s.kind === "risk");
  const con = signals.find((s) => s.kind === "con");
  const pro = signals.find(
    (s) =>
      s.kind === "pro" &&
      s.id !== "super" &&
      s.id !== "good", // these duplicate the DealChip
  );
  const out: Signal[] = [];
  if (risk) out.push(risk);
  if (con) out.push(con);
  if (pro && out.length < 2) out.push(pro);
  return out.slice(0, 2);
}

/**
 * Generates a concise AI-style one-liner verdict for a listing card.
 * Purely deterministic from available signals — no LLM call needed.
 */
function generateDealOneliner(deal: Deal, numberLocale: string): string | null {
  const rating = (deal.dealRating ?? "unknown") as DealRating;
  const pct = deal.fairPriceEur && deal.dealSavingsEur && deal.dealSavingsEur > 0
    ? Math.round((deal.dealSavingsEur / deal.fairPriceEur) * 100)
    : 0;
  const kpy = (() => {
    if (deal.currentKm == null || deal.currentYear == null) return null;
    const age = Math.max(1, new Date().getFullYear() - deal.currentYear);
    return Math.round(deal.currentKm / age);
  })();
  const days = daysOnMarket(deal);

  if (deal.savingsSuspect) return "Économie suspecte — vérifier la cohérence";
  if (rating === "super" && pct >= 15) return `${pct}% sous le marché · opportunité rare`;
  if (rating === "super") return "Prix exceptionnellement bas pour ce modèle";
  if (rating === "good" && kpy != null && kpy < 8000) return "Bon prix + faible kilométrage";
  if (rating === "good" && deal.recentPriceDropEur && deal.recentPriceDropEur > 500) return "Prix récemment baissé · bonne affaire";
  if (rating === "good") return "En dessous du prix de marché";
  if (rating === "high" && days != null && days > 60) return `En vente depuis ${days}j · négociable`;
  if (rating === "over") return "Prix au-dessus du marché";
  if (kpy != null && kpy < 6000 && (deal.currentKm ?? 0) > 3000) {
    return `Seulement ${new Intl.NumberFormat(numberLocale).format(kpy)} km/an`;
  }
  if (days != null && days <= 1) return "Vient d'arriver sur le marché";
  return null;
}

function getDealPrice(deal: Deal, currency: ReportingCurrency): number | null {
  if (currency === "USD") return deal.currentPriceUsd ?? null;
  if (currency === "CHF") return deal.currentPriceChf ?? null;
  return deal.currentPriceEur;
}

export function ListView({
  deals,
  loading,
  currency = "EUR",
  onExclude,
  excludingId,
}: {
  deals: Deal[];
  loading: boolean;
  currency?: ReportingCurrency;
  onExclude?: (listingId: string) => void;
  excludingId?: string;
}): React.JSX.Element {
  const { t } = useT();
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-4">
      <div className="flex items-center justify-end mb-4">
        <div className="inline-flex h-9 rounded-lg border border-[var(--divider-strong)] overflow-hidden bg-[var(--surface-page)]">
          <button
            onClick={() => setViewMode("cards")}
            className={cn(
              "w-9 flex items-center justify-center transition-colors",
              viewMode === "cards" ? "bg-[var(--surface-hover)] text-ink-primary" : "text-ink-tertiary hover:text-ink-secondary",
            )}
            title="Grille"
          >
            <GridIcon />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={cn(
              "w-9 flex items-center justify-center transition-colors",
              viewMode === "table" ? "bg-[var(--surface-hover)] text-ink-primary" : "text-ink-tertiary hover:text-ink-secondary",
            )}
            title="Tableau"
          >
            <TableIcon />
          </button>
        </div>
      </div>

      {viewMode === "cards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {loading && Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && deals.map((d, i) => (
            <div
              key={d.id}
              className="animate-[fadeInUp_0.3s_ease-out_both]"
              style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}
            >
              <DealCard deal={d} currency={currency} onExclude={onExclude} excluding={excludingId === d.id} />
            </div>
          ))}
          {!loading && deals.length === 0 && (
            <div className="col-span-full py-20 text-center text-ink-tertiary text-sm">{t("web.list.empty")}</div>
          )}
        </div>
      )}

      {viewMode === "table" && (
        <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-lg border border-[var(--divider-weak)] bg-[var(--surface-card)]">
          <table className="w-full text-sm border-collapse min-w-[640px]">
            <thead>
              <tr className="text-left border-b border-[var(--divider-weak)] bg-[var(--surface-sunken)]">
                <th className="w-[96px] px-3 py-3" />
                <th className="px-3 py-3 text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">{t("web.list.col.deal")}</th>
                <th className="px-3 py-3 text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">{t("web.list.col.title")}</th>
                <th className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">{t("web.list.col.year")}</th>
                <th className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">km</th>
                <th className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">{t("web.list.col.price")}</th>
                <th className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-ink-tertiary font-medium">{t("web.list.col.savings")}</th>
                <th className="w-[48px] px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
              {!loading && deals.map((d) => (
                <TableRow key={d.id} deal={d} currency={currency} onExclude={onExclude} excluding={excludingId === d.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ExcludeButton({
  listingId,
  onExclude,
  excluding,
  className,
}: {
  listingId: string;
  onExclude?: (id: string) => void;
  excluding?: boolean;
  className?: string;
}): React.JSX.Element | null {
  if (!onExclude) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onExclude(listingId);
      }}
      disabled={excluding}
      title="Masquer de ce scan"
      className={cn(
        "flex items-center justify-center h-8 w-8 rounded-md",
        "text-ink-disabled hover:text-red-500 hover:bg-red-50",
        "transition-all active:scale-[0.92] disabled:opacity-30",
        className,
      )}
    >
      {excluding ? (
        <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      )}
    </button>
  );
}

function DealCard({
  deal,
  currency,
  onExclude,
  excluding,
}: {
  deal: Deal;
  currency: ReportingCurrency;
  onExclude?: (id: string) => void;
  excluding?: boolean;
}): React.JSX.Element {
  const { t, numberLocale } = useT();
  const compare = useCompareOptional();
  const inCompare = compare?.isInCompare(deal.id) ?? false;
  const rating = (deal.dealRating ?? "unknown") as DealRating;
  const savings = deal.dealSavingsEur != null && deal.dealSavingsEur > 0;
  const price = getDealPrice(deal, currency);
  const pctBelow = deal.fairPriceEur && deal.dealSavingsEur && deal.dealSavingsEur > 0
    ? Math.round((deal.dealSavingsEur / deal.fairPriceEur) * 100)
    : 0;
  const days = daysOnMarket(deal);
  const utils = trpc.useUtils();
  const prefetchListing = useCallback((listingId: string) => {
    void utils.listings.byId.prefetch({ id: listingId }, { staleTime: 30_000 });
  }, [utils]);

  const cardSignals = React.useMemo(
    () =>
      pickCardSignals(
        computeSignals(
          {
            dealRating: deal.dealRating ?? "unknown",
            dealSavingsEur: deal.dealSavingsEur ?? null,
            dealSavingsRawEur: deal.dealSavingsRawEur ?? null,
            fairPriceEur: deal.fairPriceEur ?? null,
            fairPriceConfidence: deal.fairPriceConfidence ?? null,
            savingsSuspect: deal.savingsSuspect ?? false,
            currentPriceEur: deal.currentPriceEur ?? null,
            currentKm: deal.currentKm ?? null,
            currentYear: deal.currentYear ?? null,
            firstSeenAt: deal.firstSeenAt ?? null,
            recentPriceDropEur: deal.recentPriceDropEur ?? null,
            photoCount: deal.photoCount ?? null,
            sellerType: deal.sellerType ?? null,
            dupCount: deal.dupCount ?? null,
          },
          { t: t as (k: string, v?: Record<string, string | number>) => string, numberLocale },
        ),
      ),
    [deal, t, numberLocale],
  );

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-xl border bg-[var(--surface-card)] overflow-hidden transition-all duration-200 hover:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)]",
        inCompare
          ? "border-accent ring-1 ring-accent/30"
          : "border-[var(--divider-weak)] hover:border-[var(--divider-strong)]",
      )}
    >
      <Link to={`/listing/${deal.id}`} className="flex flex-col flex-1" onMouseEnter={() => prefetchListing(deal.id)}>
        {/* Thumbnail */}
        <div className="relative w-full aspect-[16/10] bg-[var(--surface-sunken)] overflow-hidden">
          {deal.thumbnailUrl ? (
            <img
              src={deal.thumbnailUrl}
              alt={deal.currentTitle ?? ""}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-ink-disabled">
              <CarPlaceholder />
            </div>
          )}
          {/* Deal badge */}
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
            <DealChip rating={rating} size="sm" />
            {days != null && days <= 2 && (
              <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-[4px] bg-deal-super/90 text-white text-[10px] font-semibold backdrop-blur-sm shadow-sm">
                ✦ Nouveau
              </span>
            )}
            {deal.recentPriceDropEur != null && deal.recentPriceDropEur > 500 && (
              <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-[4px] bg-blue-600/90 text-white text-[10px] font-semibold backdrop-blur-sm shadow-sm">
                ↓ Baisse
              </span>
            )}
          </div>
          {/* Country flag */}
          {deal.currentCountry && (
            <span className="absolute bottom-2 right-2 mono text-[10px] uppercase bg-black/60 text-white/90 px-1.5 py-0.5 rounded-[3px] tracking-wide backdrop-blur-sm">
              {deal.currentCountry}
            </span>
          )}
          {/* Source strip */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-2.5 py-1.5 bg-gradient-to-t from-black/50 to-transparent">
            <span className="text-[10px] text-white/80 font-medium">
              {deal.source?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
            {(deal.dupCount ?? 1) > 1 && (
              <span className="text-[10px] bg-white/20 text-white/90 px-1.5 py-0.5 rounded-[3px] font-medium backdrop-blur-sm">
                {deal.dupCount} sources
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col p-3.5 gap-2">
          <h3 className="text-[13px] font-medium text-ink-primary leading-snug line-clamp-2 min-h-[2.4em]">
            {deal.currentTitle ?? "—"}
          </h3>
          {(() => {
            const oneliner = generateDealOneliner(deal, numberLocale);
            return oneliner ? (
              <p className="text-[11px] text-ink-tertiary italic leading-tight line-clamp-1">{oneliner}</p>
            ) : null;
          })()}
          <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-ink-tertiary mono">
            {deal.currentYear && <span>{deal.currentYear}</span>}
            {deal.currentKm != null && (
              <>
                <span className="opacity-30">·</span>
                <span>{formatKm(deal.currentKm, numberLocale)}</span>
              </>
            )}
            {deal.currentEngineCc && (
              <>
                <span className="opacity-30">·</span>
                <span>{(deal.currentEngineCc / 1000).toFixed(1)}L</span>
              </>
            )}
          </div>
          {deal.features && (deal.features.lift || deal.features.ccm || deal.features.magnaride || deal.features.carbonExt || deal.features.carbonSeats) && (
            <div className="flex items-center gap-1 flex-wrap">
              {deal.features.lift && <FeaturePill label="Lift" />}
              {deal.features.ccm && <FeaturePill label="CCM" />}
              {deal.features.magnaride && <FeaturePill label="Magnaride" />}
              {deal.features.carbonExt && <FeaturePill label="Carbone" />}
              {deal.features.carbonSeats && <FeaturePill label="Sièges sport" />}
              {deal.features.sportExhaust && <FeaturePill label="Échap. sport" />}
            </div>
          )}

          <div className="mt-auto pt-2.5 border-t border-[var(--divider-weak)]">
            <div className="flex items-end justify-between">
              <span className="text-[17px] font-bold text-ink-primary mono leading-none tracking-tight">
                {formatPrice(price, { locale: numberLocale, currency })}
              </span>
              {savings && !deal.savingsSuspect && (
                <span className="text-[12px] font-semibold text-deal-super mono">
                  −{formatPrice(deal.dealSavingsEur!, { compact: true, locale: numberLocale, currency: "EUR" })}
                </span>
              )}
              {days != null && days <= 2 && (
                <span className="text-[10px] mono text-deal-super uppercase tracking-wider">
                  Nouveau
                </span>
              )}
            </div>
            {pctBelow > 0 && !deal.savingsSuspect && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1 bg-[var(--surface-sunken)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-deal-super/60 rounded-full"
                    style={{ width: `${Math.min(pctBelow * 2.5, 100)}%` }}
                  />
                </div>
                <span className="text-[10px] mono text-deal-super font-semibold whitespace-nowrap">
                  −{pctBelow}%
                </span>
              </div>
            )}
            {cardSignals.length > 0 && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {cardSignals.map((s) => (
                  <span
                    key={s.id}
                    title={s.detail ?? undefined}
                    className={cn(
                      "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[10px] font-medium",
                      s.kind === "risk"
                        ? "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
                        : s.kind === "con"
                          ? "bg-deal-over/10 text-deal-over"
                          : "bg-[var(--surface-sunken)] text-ink-secondary",
                    )}
                  >
                    {s.kind === "risk" ? "⚠" : s.kind === "con" ? "−" : "+"} {s.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
      <div
        className={cn(
          "absolute top-2 right-2 z-10 flex items-center gap-1 transition-opacity",
          inCompare ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
      >
        <CompareToggleButton deal={deal} className="bg-white/80 backdrop-blur-sm" />
        <ExcludeButton
          listingId={deal.id}
          onExclude={onExclude}
          excluding={excluding}
          className="bg-white/80 backdrop-blur-sm rounded-md"
        />
      </div>
    </div>
  );
}

function TableRow({
  deal,
  currency,
  onExclude,
  excluding,
}: {
  deal: Deal;
  currency: ReportingCurrency;
  onExclude?: (id: string) => void;
  excluding?: boolean;
}): React.JSX.Element {
  const { numberLocale } = useT();
  const compare = useCompareOptional();
  const utils = trpc.useUtils();
  const inCompare = compare?.isInCompare(deal.id) ?? false;
  const rating = (deal.dealRating ?? "unknown") as DealRating;
  const price = getDealPrice(deal, currency);
  return (
    <tr
      className={cn(
        "border-b border-[var(--divider-weak)] last:border-b-0",
        "hover:bg-[var(--surface-hover)] cursor-pointer transition-colors",
        inCompare && "bg-accent/5",
      )}
      onMouseEnter={() => void utils.listings.byId.prefetch({ id: deal.id }, { staleTime: 30_000 })}
      onClick={() => {
        window.location.href = `/listing/${deal.id}`;
      }}
    >
      <td className="px-3 py-2">
        {deal.thumbnailUrl ? (
          <img
            src={deal.thumbnailUrl}
            alt=""
            className="w-[80px] h-[56px] object-cover rounded-md"
            loading="lazy"
          />
        ) : (
          <div className="w-[80px] h-[56px] bg-[var(--surface-sunken)] rounded-md flex items-center justify-center text-ink-disabled">
            <CarPlaceholder />
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <DealChip rating={rating} size="sm" />
      </td>
      <td className="px-3 py-2 text-ink-primary truncate max-w-[260px] text-[13px]">
        {deal.currentTitle ?? "—"}
      </td>
      <td className="px-3 py-2 text-right mono text-[13px] text-ink-secondary">
        {deal.currentYear ?? "—"}
      </td>
      <td className="px-3 py-2 text-right mono text-[13px] text-ink-secondary">
        {deal.currentKm != null ? formatKm(deal.currentKm, numberLocale) : "—"}
      </td>
      <td className="px-3 py-2 text-right mono text-[13px] font-semibold text-ink-primary">
        {formatPrice(price, { locale: numberLocale, currency })}
      </td>
      <td className="px-3 py-2 text-right mono text-[13px] text-deal-super font-medium">
        {deal.dealSavingsEur != null && deal.dealSavingsEur > 0 && !deal.savingsSuspect
          ? `−${formatPrice(deal.dealSavingsEur, { compact: true, locale: numberLocale, currency: "EUR" })}`
          : deal.savingsSuspect
            ? <span className="text-amber-600" title="Estimation à confirmer">·</span>
            : "—"}
      </td>
      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-0.5">
          <CompareToggleButton deal={deal} />
          <ExcludeButton listingId={deal.id} onExclude={onExclude} excluding={excluding} />
        </div>
      </td>
    </tr>
  );
}

function SkeletonRow(): React.JSX.Element {
  return (
    <tr className="border-b border-[var(--divider-weak)] h-[72px]">
      <td className="px-3 py-2">
        <div className="w-[80px] h-[56px] bg-[var(--surface-hover)] rounded-md animate-pulse opacity-40" />
      </td>
      <td colSpan={7} className="px-3">
        <div className="h-3 w-3/4 bg-[var(--surface-hover)] rounded animate-pulse opacity-40" />
      </td>
    </tr>
  );
}

function SkeletonCard(): React.JSX.Element {
  return (
    <div className="flex flex-col rounded-xl border border-[var(--divider-weak)] overflow-hidden">
      <div className="w-full aspect-[16/10] skeleton-shimmer" />
      <div className="p-3.5 space-y-3">
        <div className="h-3 w-3/4 skeleton-shimmer rounded" />
        <div className="h-2.5 w-1/2 skeleton-shimmer rounded" />
        <div className="h-5 w-1/3 skeleton-shimmer rounded" />
      </div>
    </div>
  );
}

function FeaturePill({ label }: { label: string }): React.JSX.Element {
  return (
    <span className="inline-flex items-center h-[18px] px-1.5 rounded bg-accent/10 text-accent text-[9px] font-semibold uppercase tracking-wide">
      {label}
    </span>
  );
}

function CarPlaceholder(): React.JSX.Element {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-25">
      <path d="M5 17h2m10 0h2M3 11l1-5h2l2 3h8l2-3h2l1 5" />
      <rect x="3" y="11" width="18" height="6" rx="2" />
      <circle cx="7" cy="17" r="1" />
      <circle cx="17" cy="17" r="1" />
    </svg>
  );
}

function GridIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="1" width="6" height="6" rx="1.5" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" />
    </svg>
  );
}

function TableIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="2" width="14" height="2.5" rx="0.75" />
      <rect x="1" y="6.75" width="14" height="2.5" rx="0.75" />
      <rect x="1" y="11.5" width="14" height="2.5" rx="0.75" />
    </svg>
  );
}
